import { useMutation } from "@tanstack/react-query";
import { Link2, Plus, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import { useIssueLinkState } from "../../lib/issue-modal";
import type { IssueDetail } from "../../types";
import { SectionHeader } from "../page";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const RELATION_LABELS: Record<string, { outbound: string; inbound: string }> = {
  duplicate: { outbound: "Duplicate of", inbound: "Duplicated by" },
  related: { outbound: "Related to", inbound: "Related to" },
  blocks: { outbound: "Blocks", inbound: "Blocked by" },
};

export function RelationsBlock({
  issue,
  onChanged,
  readOnly = false,
}: {
  issue: IssueDetail;
  onChanged: () => void;
  readOnly?: boolean;
}) {
  const linkState = useIssueLinkState();
  const [adding, setAdding] = useState<null | "related" | "blocks" | "duplicate">(null);
  const [ref, setRef] = useState("");
  const add = useMutation({
    mutationFn: () => api.addRelation(issue.identifier, ref.trim(), adding!),
    onSuccess: () => {
      setAdding(null);
      setRef("");
      onChanged();
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Could not link issues"),
  });
  const remove = useMutation({
    mutationFn: (relation: IssueDetail["relations"][number]) =>
      api.removeRelation(issue.identifier, relation.issue.identifier, relation.type),
    onSuccess: onChanged,
  });

  if (readOnly && issue.relations.length === 0) return null;

  return (
    <section aria-label="Relations">
      <SectionHeader
        title="Relations"
        count={issue.relations.length || undefined}
        className="mb-1"
        actions={
          !readOnly &&
          (["related", "blocks", "duplicate"] as const).map((type) => (
            <Button
              key={type}
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-xs text-muted-foreground capitalize"
              onClick={() => setAdding(type)}
            >
              <Plus className="size-3" /> {type === "duplicate" ? "Duplicate" : type}
            </Button>
          ))
        }
      />
      {adding && (
        <form
          className="mb-2 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (ref.trim()) add.mutate();
          }}
        >
          <Input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder={`Issue identifier, e.g. BUG-12 (${adding})`}
            className="h-8 min-w-0 flex-1 font-mono text-xs sm:w-64 sm:flex-none"
            autoFocus
          />
          <Button type="submit" size="sm" disabled={add.isPending}>
            Link
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(null)}>
            Cancel
          </Button>
        </form>
      )}
      {issue.relations.length > 0 ? (
        <ul className="-mx-2">
          {issue.relations.map((relation) => (
            <li
              key={`${relation.type}:${relation.direction}:${relation.issue.id}`}
              className="group/row flex min-h-8 items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors duration-(--motion-dur) hover:bg-muted/60"
            >
              <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="shrink-0 text-xs whitespace-nowrap text-muted-foreground">
                {RELATION_LABELS[relation.type]?.[relation.direction] ?? relation.type}
              </span>
              <Link
                to={`/issues/${relation.issue.identifier}`}
                state={linkState}
                className="shrink-0 font-mono text-xs whitespace-nowrap hover:underline"
              >
                {relation.issue.identifier}
              </Link>
              <Link
                to={`/issues/${relation.issue.identifier}`}
                state={linkState}
                tabIndex={-1}
                className="min-w-0 truncate decoration-border underline-offset-[3px] group-hover/row:underline"
              >
                {relation.issue.title}
              </Link>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remove relation"
                  className="ml-auto size-5 shrink-0 pointer-fine:opacity-0 pointer-fine:group-hover/row:opacity-100 pointer-fine:focus-visible:opacity-100"
                  onClick={() => remove.mutate(relation)}
                >
                  <X className="size-3" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        !adding && <p className="text-xs text-muted-foreground">Nothing linked.</p>
      )}
    </section>
  );
}
